import type {CellFactory, EditorFactory, Field, FieldKind} from "./Field.js";
import type {JsonValue} from "./types.js";

/** The value type each field kind hands to a renderer. */
export interface FieldKindValues {
    string: string;
    number: number;
    boolean: boolean;
    date: Date;
    timestamp: Date;
    duration: number;
    enum: string;
    json: JsonValue;
    file: File;
    relation: unknown;
    relationList: unknown[];
}

export class MissingPresentationError extends Error {
    constructor(role: "editor" | "cell", kind: FieldKind, presentationKey: string | undefined, fieldName: string) {
        const target = presentationKey === undefined
            ? `field kind "${kind}"`
            : `presentation key "${presentationKey}"`;
        super(`No ${role} registered for ${target} (field "${fieldName}")`);
        this.name = "MissingPresentationError";
    }
}

/**
 * Binds field schema to renderers without the model layer ever importing a renderer.
 * The application registers its own component factories at bootstrap; JuiS only resolves them.
 *
 * This is the *fallback* for fields that render themselves the plain way their kind always does
 * (a date formats the same everywhere, say). A field with its own `withCell`/`withEditor` is
 * resolved to that instead — see {@link Field.getCell} — so a model stays the one place to look
 * for how one of its fields is drawn, while common kinds still need registering only once.
 *
 * `TNode` is whatever the application's renderers produce, `TEntity` its entity base type.
 */
export class PresentationRegistry<TNode, TEntity = unknown> {
    readonly #editorsByKind = new Map<FieldKind, unknown>();
    readonly #cellsByKind = new Map<FieldKind, unknown>();
    readonly #editorsByKey = new Map<string, unknown>();
    readonly #cellsByKey = new Map<string, unknown>();

    registerEditor<K extends FieldKind>(kind: K, factory: EditorFactory<FieldKindValues[K], TEntity, TNode>): this {
        this.#editorsByKind.set(kind, factory);
        return this;
    }

    registerCell<K extends FieldKind>(kind: K, factory: CellFactory<FieldKindValues[K], TEntity, TNode>): this {
        this.#cellsByKind.set(kind, factory);
        return this;
    }

    registerEditorFor<TValue>(presentationKey: string, factory: EditorFactory<TValue, TEntity, TNode>): this {
        this.#editorsByKey.set(presentationKey, factory);
        return this;
    }

    registerCellFor<TValue>(presentationKey: string, factory: CellFactory<TValue, TEntity, TNode>): this {
        this.#cellsByKey.set(presentationKey, factory);
        return this;
    }

    getEditor<TValue>(field: Field<TValue, boolean>): EditorFactory<TValue, TEntity, TNode> {
        const own = field.getEditor<TEntity, TNode>();
        if (own !== undefined) {
            return own;
        }
        const factory = this.#lookup(this.#editorsByKey, this.#editorsByKind, field);
        if (factory === undefined) {
            throw new MissingPresentationError("editor", field.kind, field.config.presentationKey, field.name ?? "unnamed");
        }
        // The registry is heterogeneous by nature: the stored factory was registered against this
        // field's kind or presentation key, which TypeScript cannot express without existential types.
        return factory as EditorFactory<TValue, TEntity, TNode>;
    }

    getCell<TValue>(field: Field<TValue, boolean>): CellFactory<TValue, TEntity, TNode> {
        const own = field.getCell<TEntity, TNode>();
        if (own !== undefined) {
            return own;
        }
        const factory = this.#lookup(this.#cellsByKey, this.#cellsByKind, field);
        if (factory === undefined) {
            throw new MissingPresentationError("cell", field.kind, field.config.presentationKey, field.name ?? "unnamed");
        }
        return factory as CellFactory<TValue, TEntity, TNode>;
    }

    hasEditor<TValue>(field: Field<TValue, boolean>): boolean {
        return field.getEditor<TEntity, TNode>() !== undefined
            || this.#lookup(this.#editorsByKey, this.#editorsByKind, field) !== undefined;
    }

    hasCell<TValue>(field: Field<TValue, boolean>): boolean {
        return field.getCell<TEntity, TNode>() !== undefined
            || this.#lookup(this.#cellsByKey, this.#cellsByKind, field) !== undefined;
    }

    #lookup<TValue>(byKey: Map<string, unknown>, byKind: Map<FieldKind, unknown>, field: Field<TValue, boolean>): unknown {
        const key = field.config.presentationKey;
        return key === undefined ? byKind.get(field.kind) : byKey.get(key);
    }
}
